import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import InputField from '../../commonComponent/InputField';
import { z } from 'zod';
import { ToastContainer, toast } from 'react-toastify';
import { IoWarning, IoCloseCircle, IoTrash } from 'react-icons/io5';
import 'react-toastify/dist/ReactToastify.css';
import { getCurrentUser } from '../Authentication/auth';
import { NavLink } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

const userSchema = z.object({
  firstname: z.string().nonempty('First name is required.').max(10, 'Cannot exceed 10 characters.'),
  lastname: z.string().nonempty('Last name is required.').max(10, 'Cannot exceed 10 characters.'),
  email: z.string().email('Invalid email address.').nonempty('Email is required.'),
  username: z.string().nonempty('Username is required.'),
  role: z.string().nonempty('Role is required.'),
});

const EditUserPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const userFromState = location.state?.user;

  const [userData, setUserData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    username: '',
    mobile_number: '',
    roles: [],
    role: '',
  });
  const [errors, setErrors] = useState({});
  const [allRoles, setAllRoles] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedRolesToDelete, setSelectedRolesToDelete] = useState([]);
  const [showRoleDeleteConfirm, setShowRoleDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (userFromState) {
          const userRoles = Array.isArray(userFromState.roles)
            ? userFromState.roles
            : userFromState.role
              ? [userFromState.role]
              : [];

          setUserData({
            firstname: userFromState.firstname || '',
            lastname: userFromState.lastname || '',
            email: userFromState.email || '',
            username: userFromState.username || '',
            mobile_number: userFromState.mobile_number || '',
            roles: userRoles,
            role: userRoles[0] || '',
          });
        } else {
          const user = await getCurrentUser();
          const userEmail = user?.email;

          if (userEmail) {
            const usersResponse = await fetch(
              `${AQ_BASE_URL}/aq/api/getUsers?email=${encodeURIComponent(userEmail)}`
            );

            if (!usersResponse.ok) {
              throw new Error('Failed to fetch users.');
            }

            const usersData = await usersResponse.json();
            const foundUser = usersData.find((user) => user.id === parseInt(userId));

            if (foundUser) {
              const userRoles = Array.isArray(foundUser.roles)
                ? foundUser.roles
                : foundUser.roles
                  ? [foundUser.roles]
                  : [];

              setUserData({
                firstname: foundUser.first_name || '',
                lastname: foundUser.last_name || '',
                email: foundUser.email || '',
                username: foundUser.username || '',
                mobile_number: foundUser.mobile_number || '',
                roles: userRoles,
                role: userRoles[0] || '',
              });
            } else {
              throw new Error('User not found.');
            }
          }
        }

        const user = await getCurrentUser();
        const userEmail = user?.email;

        if (userEmail) {
          const rolesResponse = await fetch(
            `${AQ_BASE_URL}/aq/api/roles/getAllRoles?email=${encodeURIComponent(userEmail)}`
          );

          if (rolesResponse.ok) {
            const rolesData = await rolesResponse.json();
            setAllRoles(rolesData);
          } else {
            console.error('Failed to fetch roles');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error.message);
        toast.error('Failed to load user data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, userFromState]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));
    }
  };

  const handleRoleChipClick = (roleName) => {
    setSelectedRolesToDelete((prev) => {
      if (prev.includes(roleName)) {
        return prev.filter((r) => r !== roleName);
      } else {
        return [...prev, roleName];
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const userDataWithStringMobile = {
        ...userData,
        mobile_number: String(userData.mobile_number),
      };

      userSchema.parse(userDataWithStringMobile);

      const user = await getCurrentUser();
      const userEmail = user?.email;

      const userPayload = {
        first_name: userData.firstname,
        last_name: userData.lastname,
        username: userData.username,
        email: userData.email,
        mobile_number: userDataWithStringMobile.mobile_number,
        role: [userData.role],
      };

      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/editUser/${userId}?email=${encodeURIComponent(userEmail)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userPayload),
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success('User updated successfully!');
        setTimeout(() => {
          navigate('/dashboard/mdm/users');
        }, 1000);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to update user.');
      }
    } catch (error) {
      console.error('Validation or API error:', error);
      if (error.errors) {
        const formattedErrors = {};
        error.errors.forEach(({ path, message }) => {
          formattedErrors[path[0]] = message;
        });
        setErrors(formattedErrors);
        toast.error('Please fix the validation errors.');
      }
    }
  };

  const handleDeleteSelectedRoles = async () => {
    if (selectedRolesToDelete.length === 0) {
      toast.error('Please select at least one role to delete.');
      return;
    }

    try {
      const user = await getCurrentUser();
      const userEmail = user?.email;

      // Delete each selected role one by one
      const deletePromises = selectedRolesToDelete.map((roleName) =>
        fetch(
          `${AQ_BASE_URL}/aq/api/deleteRole?user_id=${userId}&role_name=${roleName}&email=${encodeURIComponent(
            userEmail
          )}`,
          {
            method: 'DELETE',
            headers: {
              accept: 'application/json',
            },
          }
        )
      );

      const responses = await Promise.all(deletePromises);
      const allSuccessful = responses.every((response) => response.ok);

      if (allSuccessful) {
        toast.success('Selected roles deleted successfully!');

        // Update local state to remove deleted roles
        setUserData((prevData) => ({
          ...prevData,
          roles: prevData.roles.filter((role) => !selectedRolesToDelete.includes(role)),
        }));

        // Clear selected roles
        setSelectedRolesToDelete([]);

        // If primary role was deleted, update it
        if (selectedRolesToDelete.includes(userData.role)) {
          const remainingRoles = userData.roles.filter(
            (role) => !selectedRolesToDelete.includes(role)
          );
          if (remainingRoles.length > 0) {
            setUserData((prev) => ({ ...prev, role: remainingRoles[0] }));
          } else {
            setUserData((prev) => ({ ...prev, role: '' }));
          }
        }

        // Close confirmation modal
        setShowRoleDeleteConfirm(false);
      } else {
        toast.error('Failed to delete some roles.');
      }
    } catch (error) {
      console.error('Error deleting roles:', error);
      toast.error('An error occurred while deleting roles.');
    }
  };

  const confirmDelete = async () => {
    try {
      const user = await getCurrentUser();
      const userEmail = user?.email;

      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/deleteUser?user_id=${userId}&email=${encodeURIComponent(userEmail)}`,
        {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete the user.');
      }

      if (result.success) {
        toast.success('User deleted successfully!');
        setTimeout(() => {
          navigate('/dashboard/mdm/users');
        }, 1000);
      } else {
        toast.error(result.message || 'Failed to delete user.');
      }
    } catch (error) {
      console.error('Error deleting user:', error.message);
      toast.error('Error deleting user.');
    } finally {
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center h-64">
        <div className="text-lg">Loading user data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <NavLink
        to="/dashboard/mdm/users"
        className="flex items-center text-gray-400 hover:text-black mb-6"
      >
        <FaArrowLeft className="mr-3 text-md" />
        <span className="text-lg">Back to user management</span>
      </NavLink>

      <div className="container mx-auto p-4">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        {/* Form Fields */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-bold mb-6">Edit User</h2>

          <div className="flex gap-4 mb-4">
            <div className="w-1/2">
              <InputField
                id="firstname"
                name="firstname"
                label="First Name"
                type="text"
                placeholder="First Name"
                value={userData.firstname}
                onChange={handleChange}
                error={errors.firstname}
                disabled={true}
              />
            </div>
            <div className="w-1/2">
              <InputField
                id="lastname"
                name="lastname"
                label="Last Name"
                type="text"
                placeholder="Last Name"
                value={userData.lastname}
                onChange={handleChange}
                error={errors.lastname}
                disabled={true}
              />
            </div>
            <div className="w-1/2">
              <InputField
                id="username"
                name="username"
                label="User Name"
                type="text"
                placeholder="Username"
                value={userData.username}
                onChange={handleChange}
                error={errors.username}
                disabled={true}
              />
            </div>
          </div>

          <div className="flex gap-4 mb-6">
            <div className="w-1/2">
              <InputField
                id="email"
                name="email"
                label="Email"
                type="email"
                placeholder="Email"
                value={userData.email}
                onChange={handleChange}
                error={errors.email}
              />
            </div>

            <div className="w-1/2">
              <InputField
                id="mobile_number"
                name="mobile_number"
                label="Phone Number"
                type="tel"
                placeholder="Phone Number"
                value={userData.mobile_number}
                onChange={handleChange}
                error={errors.mobile_number}
              />
            </div>
          </div>

          {/* Primary Role Selection - Only shows user's current roles */}
          {/* Primary Role Selection - Only shows user's current roles in bullet list */}
          <div className="w-full mb-3">
            <h1 className="font-medium text-gray-950 text-sm mb-3">Current Role</h1>

            {/* Display current roles as bullet points */}
            <div className="mb-4">
              <p className="text-gray-600 text-sm mb-2">Current user roles:</p>
              {userData.roles.length > 0 ? (
                <ul className="space-y-2">
                  {userData.roles.map((role, index) => (
                    <li key={index} className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-gray-900 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-800">{role}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 italic text-sm">No roles assigned</p>
              )}
            </div>

            {/* Dropdown for selecting primary role */}
            {/* <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Select Primary Role
              </label>
              <select
                name="role"
                value={userData.role}
                onChange={handleChange}
                className="w-full border border-gray-300 text-sm rounded-lg py-2.5 px-3 cursor-pointer text-gray-700 bg-white focus:border-black focus:ring-1 focus:ring-black"
              >
                <option value="" disabled>
                  Choose primary role
                </option>
                {userData.roles.length > 0 ? (
                  userData.roles.map((role, index) => (
                    <option key={index} value={role}>
                      {role}
                    </option>
                  ))
                ) : (
                  <option value="">No roles available</option>
                )}
              </select>
              {errors.role && <p className="text-red-500 text-sm mt-1">{errors.role}</p>}
            </div> */}
          </div>
          <hr className="border-t border-gray-200 mb-3"></hr>
          {/* Delete Roles Section - Unique Chip/Tag Design */}
          <div className="w-full mb-8">
            <div className="flex items-center justify-between mb-3">
              <h1 className="font-medium text-gray-950 text-sm">Delete User Roles</h1>
              {selectedRolesToDelete.length > 0 && (
                <span className="text-xs font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                  {selectedRolesToDelete.length} selected
                </span>
              )}
            </div>

            <p className="text-gray-600 text-sm mb-6">
              Click on roles to select them for deletion:
            </p>

            <div className="space-y-4 mb-8">
              {userData.roles.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500 italic">No roles assigned to this user</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {userData.roles.map((role, index) => {
                    const isSelected = selectedRolesToDelete.includes(role);
                    return (
                      <button
                        key={index}
                        onClick={() => handleRoleChipClick(role)}
                        className={`
                          flex items-center gap-2 px-4 py-3 rounded-lg border transition-all duration-200
                          ${
                            isSelected
                              ? 'bg-red-100 border-red-300 text-red-700 shadow-sm'
                              : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                          }
                        `}
                      >
                        <span className="font-medium text-sm">{role}</span>
                        {isSelected ? (
                          <IoCloseCircle className="text-red-500" size={18} />
                        ) : (
                          <IoTrash className="text-gray-400" size={16} />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action Section - Only show when roles are selected */}
            {selectedRolesToDelete.length > 0 && (
              <div className="mt-6 p-5 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-red-800 mb-1">
                      Ready to Delete {selectedRolesToDelete.length} Role
                      {selectedRolesToDelete.length > 1 ? 's' : ''}
                    </h3>
                    <p className="text-xs text-red-600">
                      You have selected {selectedRolesToDelete.length} role(s) for deletion
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedRolesToDelete([])}
                    className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-100"
                    title="Clear all selections"
                  >
                    <IoCloseCircle size={20} />
                  </button>
                </div>

                <div className="mb-5">
                  <p className="text-xs font-medium text-gray-700 mb-2">Selected roles:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRolesToDelete.map((role, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white border border-red-300 rounded-full shadow-sm"
                      >
                        <span className="text-xs font-medium text-red-700">{role}</span>
                        <button
                          onClick={() => handleRoleChipClick(role)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <IoCloseCircle size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRoleDeleteConfirm(true)}
                    className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium py-3 px-6 rounded-lg text-sm shadow-sm transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <IoTrash size={16} />
                    Delete Selected Roles ({selectedRolesToDelete.length})
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <div className="flex gap-4">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2.5 px-8 rounded-lg transition-colors duration-200"
              >
                Delete User
              </button>
              <button
                onClick={handleSubmit}
                className="bg-black hover:bg-gray-800 text-white font-medium py-2.5 px-8 rounded-lg transition-colors duration-200"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>

        {/* Delete User Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-xl shadow-2xl max-w-md w-full mx-4 border border-gray-200">
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <IoWarning size={32} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Delete User</h2>
                <p className="text-gray-600 text-sm mb-6">
                  Are you sure you want to delete this user? This action is permanent and cannot be
                  undone.
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors duration-200 border border-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Roles Confirmation Modal */}
        {showRoleDeleteConfirm && (
          <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-xl shadow-2xl max-w-md w-full mx-4 border border-gray-200">
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <IoWarning size={32} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Delete {selectedRolesToDelete.length} Role
                  {selectedRolesToDelete.length > 1 ? 's' : ''}
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  Are you sure you want to delete the selected role(s)? This action cannot be
                  undone.
                </p>
              </div>

              <div className="mb-6">
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-700 mb-3">
                    The following roles will be deleted:
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                    {selectedRolesToDelete.map((role, index) => (
                      <div
                        key={index}
                        className="flex items-center p-3 bg-white rounded-lg border border-red-100 shadow-sm"
                      >
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                        <span className="text-sm font-medium text-gray-800">{role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowRoleDeleteConfirm(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors duration-200 border border-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSelectedRoles}
                  className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Delete {selectedRolesToDelete.length} Role
                  {selectedRolesToDelete.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditUserPage;

// import React, { useState, useEffect } from "react";
// import InputField from "../../commonComponent/InputField";
// import { z } from "zod";
// import { IoClose } from "react-icons/io5";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

// const userSchema = z.object({
//   firstname: z
//     .string()
//     .nonempty("First name is required.")
//     .max(10, "Cannot exceed 10 characters."),
//   lastname: z
//     .string()
//     .nonempty("Last name is required.")
//     .max(10, "Cannot exceed 10 characters."),
//   email: z
//     .string()
//     .email("Invalid email address.")
//     .nonempty("Email is required."),
//   username: z.string().nonempty("Username is required."),
//   role: z.string().nonempty("Role is required."),
// });

// const EditUser = ({ selectedUser, setShowEditUser, closeModal }) => {
//   const [userData, setUserData] = useState({
//     firstname: "",
//     lastname: "",
//     email: "",
//     username: "",
//     mobile_number: 0,
//     role: "",
//   });
//   const [errors, setErrors] = useState({});
//   const [roles, setRoles] = useState([]);

//   useEffect(() => {
//     const fetchRoles = async () => {
//       try {
//         const storedUser = localStorage.getItem("user");
//         const user = storedUser ? JSON.parse(storedUser) : null;

//         const response = await fetch(
//           `${AQ_BASE_URL}/aq/api/roles/getAllRoles?email=${encodeURIComponent(
//             user.email
//           )}`
//         );
//         if (response.ok) {
//           const result = await response.json();
//           setRoles(result);
//         } else {
//           console.error("Failed to fetch roles");
//         }
//       } catch (error) {
//         console.error("Error fetching roles:", error);
//       }
//     };

//     fetchRoles();

//     if (selectedUser && selectedUser.id) {
//       setUserData({
//         firstname: selectedUser.firstname || "",
//         lastname: selectedUser.lastname || "",
//         email: selectedUser.email || "",
//         username: selectedUser.username || "",
//         mobile_number: selectedUser.mobile_number,
//         role: selectedUser.role || "",
//       });
//     }
//   }, [selectedUser]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setUserData((prevData) => ({
//       ...prevData,
//       [name]: value,
//     }));
//   };
//   const handleSubmit = async () => {
//     try {
//       // Ensure mobile_number is treated as a string
//       const userDataWithStringMobile = {
//         ...userData,
//         mobile_number: String(userData.mobile_number), // Convert to string
//       };

//       // Validate the data using the schema
//       userSchema.parse(userDataWithStringMobile);

//       // Prepare the payload for the API
//       const userPayload = {
//         first_name: userData.firstname,
//         last_name: userData.lastname,
//         username: userData.username,
//         email: userData.email,
//         mobile_number: userDataWithStringMobile.mobile_number, // Send as string
//         role: [userData.role],
//       };

//       // Get the logged-in user's email from localStorage
//       const storedUser = localStorage.getItem("user");
//       const user = storedUser ? JSON.parse(storedUser) : null;

//       // Send the API request
//       const response = await fetch(
//         `${AQ_BASE_URL}/aq/api/editUser/${
//           selectedUser.id
//         }?email=${encodeURIComponent(user.email)}`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(userPayload),
//         }
//       );

//       // Handle the API response
//       if (response.ok) {
//         const result = await response.json();
//         toast.success("User updated successfully!");
//         closeModal();
//         setShowEditUser(false);
//         setTimeout(() => {
//           window.location.reload(); // Refresh the page after 3 seconds
//         }, 1000); // 3000 milliseconds = 3 seconds
//       } else {
//         const errorData = await response.json();
//         toast.error(errorData.message || "Something went wrong.");
//       }
//     } catch (error) {
//       console.error("Validation or API error:", error);
//       if (error.errors) {
//         const formattedErrors = {};
//         error.errors.forEach(({ path, message }) => {
//           formattedErrors[path[0]] = message;
//         });
//         setErrors(formattedErrors);
//       }
//     }
//   };

//   const handleDeleteRole = async () => {
//     try {
//       const storedUser = localStorage.getItem("user");
//       const user = storedUser ? JSON.parse(storedUser) : null;

//       const response = await fetch(
//          `${AQ_BASE_URL}/aq/api/deleteRole?user_id=${
//           selectedUser.id
//         }&role_name=${userData.role}&email=${encodeURIComponent(user.email)}`,
//         {
//           method: "DELETE",
//           headers: {
//             accept: "application/json",
//           },
//         }
//       );

//       if (response.ok) {
//         const result = await response.json();
//         toast.success("Role deleted successfully!");
//         closeModal();
//         setShowEditUser(false);
//         setTimeout(() => {
//           window.location.reload(); // Refresh the page after 3 seconds
//         }, 1000); // 3000 milliseconds = 3 seconds
//       } else {
//         const errorData = await response.json();
//         toast.error(errorData.detail || "Something went wrong.");
//       }
//     } catch (error) {
//       console.error("Error deleting role:", error);
//       toast.error("An error occurred while deleting the role.");
//     }
//   };

//   return (
//     <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm">
//       <ToastContainer />
//       <div className="bg-white p-6 rounded-lg shadow-lg w-2/4 relative">
//         {/* Close Icon */}
//         <button
//           onClick={() => {
//             setShowEditUser(false);
//             closeModal();
//           }}
//           className="absolute top-2 right-2 text-red-500 hover:text-red-700"
//         >
//           <IoClose size={24} />
//         </button>

//         <h2 className="text-lg text-center font-bold ">Edit User</h2>
//         <h3 className="text-xs text-gray-500 text-center font-semibold mb-4">
//           Editing is limited to the email, phone number, and roles of the user.
//         </h3>

//         {/* Form Fields */}
//         <div className="flex gap-4 mb-4">
//           <div className="w-1/2">
//             <InputField
//               id="firstname"
//               name="firstname"
//               label="First Name"
//               type="text"
//               placeholder="First Name"
//               value={userData.firstname}
//               onChange={handleChange}
//               error={errors.firstname}
//               disabled={true}
//             />
//           </div>
//           <div className="w-1/2">
//             <InputField
//               id="lastname"
//               name="lastname"
//               label="Last Name"
//               type="text"
//               placeholder="Last Name"
//               value={userData.lastname}
//               onChange={handleChange}
//               error={errors.lastname}
//               disabled={true}
//             />
//           </div>
//           <div className="w-1/2">
//             <InputField
//               id="username"
//               name="username"
//               label="User Name"
//               type="text"
//               placeholder="Username"
//               value={userData.username}
//               onChange={handleChange}
//               error={errors.username}
//               disabled={true}
//             />
//           </div>
//         </div>

//         <div className="flex gap-4 mb-4">
//           <div className="w-1/2">
//             <InputField
//               id="email"
//               name="email"
//               label="Email"
//               type="email"
//               placeholder="Email"
//               value={userData.email}
//               onChange={handleChange}
//               error={errors.email}
//             />
//           </div>

//           <div className="w-1/2">
//             <InputField
//               id="mobile_number"
//               name="mobile_number"
//               label="Phone Number"
//               type="tel"
//               placeholder="Phone Number"
//               value={userData.mobile_number}
//               onChange={handleChange}
//               error={errors.mobile_number}
//             />
//           </div>
//         </div>

//         <div className="w-full mb-4">
//           <h1 className="font-medium text-green-700 text-sm">User's Role</h1>
//           <select
//             name="role"
//             value={userData.role}
//             onChange={handleChange}
//             className="w-full border border-gray-300 text-xs font-semibold rounded-lg py-2 px-3 cursor-pointer text-gray-700 bg-white"
//           >
//             <option value="" disabled>
//               Select Role Type
//             </option>
//             {roles.length > 0 &&
//               roles.map((role) => (
//                 <option key={role.id} value={role.role_name}>
//                   {role.role_name}
//                 </option>
//               ))}
//           </select>
//           {errors.role && <p className="text-red-500 text-sm">{errors.role}</p>}
//         </div>

//         <div className="flex justify-end gap-8">
//           <button
//             onClick={handleSubmit}
//             className="bg-cyan-500 hover:text-black text-white font-normal py-2 px-12 rounded-lg"
//           >
//             Save
//           </button>
//           <button
//             onClick={handleDeleteRole}
//             className="bg-red-500 text-white hover:text-black font-normal py-2 px-12 rounded-lg"
//           >
//             Delete Role
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default EditUser;
